import { post, setToken, ApiError, get } from "./api";

export interface AdminSession {
  id: string;
  username: string;
  full_name: string | null;
  role?: string;
}

const SSO_BASE_URL = (
  (window as unknown as { SSO_BASE_URL?: string }).SSO_BASE_URL &&
  (window as unknown as { SSO_BASE_URL?: string }).SSO_BASE_URL !== "__SSO_BASE_URL__"
    ? (window as unknown as { SSO_BASE_URL: string }).SSO_BASE_URL
    : import.meta.env.VITE_SSO_BASE_URL ?? "https://auth.satashkent.uz"
).replace(/\/$/, "");

const SSO_CLIENT_ID =
  ((window as unknown as { SSO_CLIENT_ID?: string }).SSO_CLIENT_ID &&
  (window as unknown as { SSO_CLIENT_ID?: string }).SSO_CLIENT_ID !== "__SSO_CLIENT_ID__"
    ? (window as unknown as { SSO_CLIENT_ID: string }).SSO_CLIENT_ID
    : import.meta.env.VITE_SSO_CLIENT_ID ?? "sales-bot");

const SSO_CALLBACK_PATH = "/auth/callback";
const SSO_MODE = "admin";
const SSO_METHOD = "IDPW";

function getSsoCallbackUrl(): string {
  return `${window.location.origin}${SSO_CALLBACK_PATH}`;
}

export function startSsoFlow(): void {
  const url = new URL(`${SSO_BASE_URL}/api/auth/authorize`);
  url.searchParams.set("client_id", SSO_CLIENT_ID);
  url.searchParams.set("redirect_uri", getSsoCallbackUrl());
  url.searchParams.set("mode", SSO_MODE);
  url.searchParams.set("method", SSO_METHOD);
  window.location.assign(url.toString());
}

async function exchangeSsoCode(code: string): Promise<string> {
  const response = await fetch(`${SSO_BASE_URL}/api/auth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      code,
      client_id: SSO_CLIENT_ID,
      redirect_uri: getSsoCallbackUrl(),
    }),
  });
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new Error(
      (payload.detail as string) || (payload.error as string) || `SSO token exchange failed: ${response.status}`
    );
  }
  if (!payload.access_token) {
    throw new Error("No access token returned by SSO");
  }
  return payload.access_token as string;
}

export async function completeSsoFlow(code: string): Promise<AdminSession> {
  const ssoAccessToken = await exchangeSsoCode(code);
  const result = await post<{ access_token: string; admin: AdminSession }>("/api/auth/login", {
    access_token: ssoAccessToken,
  });
  setToken(result.access_token);
  return result.admin;
}

export async function fetchSession(): Promise<AdminSession | null> {
  try {
    const data = await get<{ admin: AdminSession }>("/api/auth/me");
    return data.admin;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function signOut(): Promise<void> {
  try {
    await fetch(`${SSO_BASE_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // best effort
  }
  setToken(null);
}
